import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useMonitoringLocation,
  useUpdateMonitoringLocationStatus,
} from "@/hooks/use-monitoring-locations";
import { useMonitoringParameters } from "@/hooks/use-monitoring-parameters";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { useFacility } from "@/hooks/use-facilities";
import { PageHeader, Breadcrumbs, Button, PageSkeleton, StatusDropdown } from "@/components/ui";
import { LocationTabs, type LocationTab } from "@/components/monitoring-locations/location-tabs";
import { LocationOverviewTab } from "@/components/monitoring-locations/location-overview-tab";
import { LocationParametersTab } from "@/components/monitoring-locations/location-parameters-tab";

type EntityStatus = components["schemas"]["EntityStatus"];

const entityStatuses: EntityStatus[] = ["active", "inactive", "archived"];

interface SearchParams {
  facilityId?: string;
}

export const Route = createFileRoute("/_authenticated/monitoring-locations/$locationId/")({
  component: UnifiedLocationDetailPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
  }),
});

function UnifiedLocationDetailPage() {
  const { t } = useTranslation(["monitoringLocations", "common", "nav", "facilities"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const { locationId } = Route.useParams();
  const { facilityId: searchFacilityId } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canUpdate = can("monitoring_locations", "update");

  const [activeTab, setActiveTab] = useState<LocationTab>("overview");

  // Use searchFacilityId for initial load, then derive from location response
  const {
    data: location,
    isLoading,
    isError,
  } = useMonitoringLocation(orgId, searchFacilityId ?? "", locationId);
  useRecentPageTitle(location?.name ?? undefined);

  // Derive facilityId from location response
  const facilityId = location?.facility_id ?? searchFacilityId ?? "";

  const { data: facility } = useFacility(orgId, facilityId);
  const facilityName = facility?.name ?? facilityId;

  const statusMutation = useUpdateMonitoringLocationStatus(orgId, facilityId, locationId);

  // Fetch parameters for stat cards on overview tab
  const paramFilters = useMemo(() => ({ limit: 200 }), []);
  const { data: paramsData } = useMonitoringParameters(location ? orgId : null, {
    mode: "location",
    facilityId,
    locationId,
    filters: paramFilters,
  });
  const allParams = useMemo(() => paramsData?.data ?? [], [paramsData]);
  const parameterCount = allParams.length;
  const withoutPermitCount = allParams.filter((p) => p.authority_type !== "permit").length;

  // Derive unique permits linked to parameters at this location
  const linkedPermits = useMemo(() => {
    const seen = new Set<string>();
    const permits: Array<{ permit_id: string; permit_number: string }> = [];
    for (const p of allParams) {
      if (p.permit_id && p.permit_number && !seen.has(p.permit_id)) {
        seen.add(p.permit_id);
        permits.push({ permit_id: p.permit_id, permit_number: p.permit_number });
      }
    }
    return permits;
  }, [allParams]);

  const handleStatusChange = (status: EntityStatus) => {
    statusMutation.mutate(
      { status },
      {
        onSuccess: () => {
          toasts.success("monitoringLocations:statusUpdated");
        },
        onError: () => toasts.error("monitoringLocations:statusUpdateFailed"),
      },
    );
  };

  const breadcrumbs = searchFacilityId
    ? [
        { label: t("facilities", { ns: "nav" }), to: "/facilities" },
        { label: facilityName, to: `/facilities/${searchFacilityId}` },
        {
          label: t("title"),
          to: `/monitoring-locations?facilityId=${searchFacilityId}`,
        },
        { label: location?.name ?? locationId },
      ]
    : [
        {
          label: t("title"),
          to: "/monitoring-locations",
        },
        { label: location?.name ?? locationId },
      ];

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !location) {
    return (
      <div className="p-8">
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader title={t("loadFailed", { ns: "facilities" })} />
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        title={location.name}
        actions={
          canUpdate ? (
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  void navigate({
                    to: "/monitoring-locations/$locationId/edit",
                    params: { locationId },
                    search: searchFacilityId ? { facilityId: searchFacilityId } : {},
                  })
                }
              >
                {t("edit")}
              </Button>
              <StatusDropdown
                currentStatus={location.status}
                statuses={entityStatuses}
                isPending={statusMutation.isPending}
                onChange={handleStatusChange}
                statusKeyPrefix="monitoringLocations:entityStatus"
              />
            </div>
          ) : undefined
        }
      />

      <div className="mt-6">
        <LocationTabs activeTab={activeTab} onTabChange={setActiveTab} />

        <div
          className="mt-4"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === "overview" && (
            <LocationOverviewTab
              location={location}
              parameterCount={parameterCount}
              withoutPermitCount={withoutPermitCount}
              linkedPermits={linkedPermits}
              onViewParameters={() => setActiveTab("parameters")}
            />
          )}
          {activeTab === "parameters" && (
            <LocationParametersTab
              orgId={orgId ?? ""}
              facilityId={facilityId}
              locationId={locationId}
            />
          )}
        </div>
      </div>
    </div>
  );
}
